import React, { useEffect, useState } from "react";
import { XIcon } from "@phosphor-icons/react";

/**
 * MeetFounderFloater — a dismissible, gently-pulsing pill that lives in the
 * bottom-left of the viewport on desktop (bottom-center on mobile) and
 * smooth-scrolls the user to the Founder's Letter excerpt on the Landing
 * page when clicked.
 *
 * Design intent:
 *   • Tiny, premium, non-annoying. Quiet, not noisy.
 *   • Disappears once the user has actually scrolled past the founder
 *     excerpt (the message has been received — stop nagging).
 *   • Dismissible; we honour that for the rest of the session.
 *   • Delay-mounted so it doesn't compete with the hero's first impression.
 *
 * Target: any element with id="meet-the-founder" on the page.
 */
export default function MeetFounderFloater() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [pastTarget, setPastTarget] = useState(false);

  // Honour prior dismissal for the session.
  useEffect(() => {
    try {
      if (sessionStorage.getItem("bidorah_meet_founder_dismissed") === "1") {
        setDismissed(true);
      }
    } catch (_) {
      /* ignore — non-critical */
    }
  }, []);

  // Reveal after a short delay so the hero gets its uninterrupted moment.
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 1600);
    return () => clearTimeout(t);
  }, []);

  // Hide once the user has scrolled meaningfully past the founder section.
  useEffect(() => {
    const onScroll = () => {
      const el = document.getElementById("meet-the-founder");
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // If the founder section's bottom is above the viewport top, the user
      // has already passed it — stop showing the floater.
      if (rect.bottom < 80) setPastTarget(true);
      else setPastTarget(false);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onClick = (e) => {
    e.preventDefault();
    const el = document.getElementById("meet-the-founder");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onDismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setDismissed(true);
    try {
      sessionStorage.setItem("bidorah_meet_founder_dismissed", "1");
    } catch (_) {
      /* ignore */
    }
  };

  if (dismissed || pastTarget) return null;

  return (
    <div
      className={`fixed z-40 left-1/2 -translate-x-1/2 sm:left-6 sm:translate-x-0 transition-all duration-500 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"
      }`}
      style={{
        // Sit comfortably above the device's gesture / nav bar by stacking
        // the safe-area inset on top of the visual gap. Uses the Android
        // wrapper's --sai-bottom var first (real measured pixel inset) and
        // falls back to the standard env() value on iOS / web.
        bottom: "calc(var(--sai-bottom, env(safe-area-inset-bottom, 0px)) + 20px)",
      }}
      data-testid="meet-founder-floater"
    >
      <button
        type="button"
        onClick={onClick}
        className="group relative flex items-center gap-3 rounded-full bg-white/95 backdrop-blur border border-zinc-200/80 shadow-[0_8px_28px_-6px_rgba(15,23,42,0.18)] hover:shadow-[0_12px_36px_-8px_rgba(29,120,171,0.35)] hover:-translate-y-0.5 transition-all duration-300 pl-1.5 pr-4 py-1.5"
        data-testid="meet-founder-floater-button"
        aria-label="Meet the founder"
      >
        {/* Photo with subtle attention pulse on the ring. */}
        <span className="relative inline-flex">
          <span className="absolute inset-0 rounded-full bg-amber-300/70 animate-ping opacity-40" />
          <img
            src="/founder/noel-campos-320.jpg"
            alt="Noel Campos"
            className="relative h-10 w-10 rounded-full object-cover ring-2 ring-amber-300"
            style={{ objectPosition: "center 22%" }}
          />
        </span>
        <span className="text-left leading-tight">
          <span className="block text-[10px] font-mono tracking-[0.18em] uppercase text-[#1D78AB]">
            From the founder
          </span>
          <span className="block text-sm font-bold text-zinc-900 group-hover:text-[#1D78AB] transition-colors">
            Meet Noel — read the letter →
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={onDismiss}
        className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-zinc-900 text-white grid place-items-center shadow-md hover:bg-zinc-700 transition-colors"
        data-testid="meet-founder-floater-dismiss"
        aria-label="Dismiss"
      >
        <XIcon size={12} weight="bold" />
      </button>
    </div>
  );
}
